import {
  OmniElement,
  OmniStyleElement,
  OmniIconElement,
  OmniTooltipElement,
  html,
  css,
  classMap,
  nothing,
  createRef,
  ref,
} from 'omni-ui';

import { MicrosoftAuthenticationHandler, AdaptiveCard } from 'omni-teams-sdk';

import { Om2TwoPaneSelect, om2alert } from 'omni-campaign-ui';

import CampaignsAPI from '../services/campaigns-api.js';
import OmniAPI from '../services/omni-api.js';

OmniStyleElement.register();
OmniIconElement.register();
OmniTooltipElement.register();
Om2TwoPaneSelect.register();

/**
 * Class representing Campaign Assign Step User.
 * @extends OmniElement
 */
export class CampaignAssignStepUser extends OmniElement {
  /**
   * @property {String} env - Application environment
   * @property {String} token - API JWT token
   * @property {Object} step - The active step detail object
   * @property {Object} readonly - Indicates whether to render as readonly
   * @property {String} clientId - The campaign client id (needed for api)
   * @property {Boolean} dropdownIsActive - Indicates whether the user assignment dropdown is active or not
   * @property {String} userTypeSelected - The type of user being selected, either 'client' or 'teams'
   * @property {Array} clientUsers - An array of all client users
   * @property {Array} selectedClientUsers - An array of selected client users for assignment
   * @property {Array} selectedTeamsUsers - An array of selected Teams users for assignment
   * @property {Boolean} isSaving - Indicates the state of the API call to save users
   */
  static get properties() {
    return {
      env: { type: String },
      token: { type: String },
      campaign: { type: Object },
      step: { type: Object },
      readonly: { type: Object },
      clientId: { type: String },
      dropdownIsActive: { type: Boolean },
      userTypeSelected: { type: String },
      clientUsers: { type: Array },
      selectedClientUsers: { type: Array },
      selectedTeamsUsers: { type: Array },
      isSaving: { type: Boolean },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .modal-card-body {
          min-height: 20.79rem; /* Keep height the same even when only skeleton is present */
        }
        .omni span.tiny-red-circle {
          display: inline-block;
          background-color: var(--color-melon);
          width: 0.857rem;
          height: 0.857rem;
          border-radius: 0.429rem;
          line-height: 0.571rem;
          color: white;
          font-size: 0.571rem;
          font-weight: bold;
          text-align: center;
          vertical-align: super;
          padding: 0.143rem 0;
          left: -1.29rem;
          position: relative;
          pointer-events: none;
        }
        .omni .dropdown-menu {
          position: absolute;
          padding-bottom: 0.29rem;
        }
        .dropdown.is-active > .dropdown-menu {
          visibility: visible;
          bottom: 0;
          right: 0;
          left: auto;
        }
        .dropdown-content {
          display: flex;
          flex-direction: column;
        }
        .omni button.dropdown-item {
          all: unset;

          color: var(--color-gray-4a);
          display: block;
          font-size: 0.875rem;
          line-height: 1.5;
          padding: 0.375rem 3rem 0.375rem 1rem;
          position: relative;
        }
        .omni button.dropdown-item:active,
        .omni button.dropdown-item:focus {
          background-color: rgba(var(--rgb-primary), 0.15);
        }
      `,
    ];
  }

  /**
   * Sets up API services and initial component state.
   */
  connectedCallback() {
    super.connectedCallback();
    this.campaignsAPI = new CampaignsAPI(this.token, this.env);
    this.omniAPI = new OmniAPI(this.env);
    this.msAuth = new MicrosoftAuthenticationHandler();
    this.dropdownIsActive = false;
    this.userTypeSelected = '';
    this.dropDownBtnRef = createRef();
    this.modalRef = createRef();
    document.addEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Cleans up resources and removes event listeners.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Initialize Microsoft auth handler
   */
  async firstUpdated() {
    await this.msAuth.init();
  }

  /**
   * If userTypeSelected changes and it's truthy, fetches client users from the API
   * or Teams users from Microsoft API, and initializes selectedClientUsers or selectedTeamsUsers
   * @param {Map} changedProperties - The changed properties with old values.
   */
  async updated(changedProperties) {
    if (changedProperties.has('userTypeSelected') && this.userTypeSelected) {
      if (!this.clientUsers && this.userTypeSelected === 'client') {
        try {
          this.clientUsers = await this.omniAPI.getClientUsers(this.clientId);
        } catch (e) {
          let message = e;
          if (e instanceof Response) {
            message = await e.text();
          }
          om2alert(
            String(message) ||
              'Failed to get client users. Please try again later.',
          );
          this.#closeModal();
        }
        this.selectedClientUsers = this.step.client_users
          .map(user =>
            this.clientUsers
              ? this.clientUsers.users.find(user2 => user2.PersonGUID === user)
              : {},
          )
          .filter(user => user !== undefined);
      }
      if (this.step.teams_users?.length) {
        try {
          const teamsUsersData = await this.msAuth.getTeamMembersDetails(
            this.step.teams_users,
          );
          this.selectedTeamsUsers = teamsUsersData.map(user => ({
            Name: user.displayName,
            PersonGUID: user.id,
          }));
        } catch (e) {
          let message = e;
          if (e instanceof Response) {
            message = await e.text();
          }
          om2alert(
            String(message) ||
              'Failed to get teams members. Please try again later.',
          );
          this.#closeModal();
        }
      }
    }
  }

  /**
   * Fetches Teams Members from Microsoft API.
   * @param {string} search - The search term to filter team members.
   * @return {Array} The list of teams members matching the search term.
   */
  // eslint-disable-next-line consistent-return
  #getTeamsMembers = async search => {
    try {
      const results = await this.msAuth.getPeople(search);
      return results.map(result => ({
        PersonGUID: result.id,
        Name: result.displayName,
      }));
    } catch (e) {
      let message = e;
      if (e instanceof Response) {
        message = await e.text();
      }
      om2alert(
        String(message) ||
          'Failed to get team members. Please try again later.',
      );
      this.#closeModal();
    }
  };

  /**
   * Handles user change event. Based on the type ('client' or 'teams'),
   * updates selectedClientUsers or selectedTeamsUsers with the new value.
   * @param {Event} event - The event, containing the new value.
   * @param {string} type - The type of users being updated, either 'client' or 'teams'.
   */
  handleUserChange({ target: { value } }, type) {
    if (type === 'client') {
      this.selectedClientUsers = value;
    } else {
      this.selectedTeamsUsers = value;
    }
  }

  /**
   * Closes the modal by resetting the userTypeSelected property.
   */
  #closeModal() {
    this.userTypeSelected = '';
    this.dropdownIsActive = false;
  }

  /**
   * Handles document click event and closes the modal if the click is outside the dropdown.
   * @param {MouseEvent} e - The click event.
   */
  #handleDocumentClick = e => {
    if (
      e.composedPath().includes(this.dropDownBtnRef.value) ||
      e.composedPath().includes(this.modalRef.value)
    )
      return;
    this.#closeModal();
  };

  #getClientName = () =>
    window.AN.sso.user.clients.find(x => x.clientId === this.clientId).orgName;

  /**
   * @param {Object[]} users - An array of users to which the card will be sent. Each user is an object with properties 'Name' and 'PersonGUID'.
   * This method creates a card for a campaign and sends it to a set of users. The card includes information about the campaign and the user's assignment.
   * The method uses `this.adaptiveCardBuilder.buildCard()` to construct the card, and `this.msAuth.sendCard()` to send it.
   */
  #sendCard = async users => {
    const stageName = this.step.framework_template.parent.name;
    const stepName = this.step.framework_template.name;
    const stepOrder = this.step.framework_template.display_order_label;
    const {
      start_date: startDate,
      end_date: endDate,
      name: campaignName,
    } = this.campaign;

    const attachmentId = crypto.randomUUID();
    const adaptiveCardBuilder = new AdaptiveCard();

    const cardContent = {
      subject: null,
      body: {
        contentType: 'html',
        content: `You have been assigned to collaborate in Omni! <b>Client: </b>${this.#getClientName()} <b>Campaign: </b>${campaignName} <b>Stage: </b> ${stageName} <b>Step: </b>${stepName} <br><br><attachment id="${attachmentId}"></attachment>`,
      },
      mentions: [],
      attachments: [
        {
          id: attachmentId,
          contentType: 'application/vnd.microsoft.card.adaptive',
          contentUrl: null,
          content: await adaptiveCardBuilder.buildCard({
            object_properties: {
              ...this.campaign,
              campaign_step_data: {
                step: `${stageName} | ${stepOrder}: ${stepName}`,
                duration: `${startDate} to ${endDate}`,
              },
              type: 'Campaign',
              url: window.location.href,
            },
          }),
          name: null,
          thumbnailUrl: null,
        },
      ],
    };

    await Promise.allSettled(
      users.map(user => {
        const teamUser = {
          displayName: user.Name,
          id: user.PersonGUID,
          type: 'People',
        };

        return this.msAuth.sendCard(cardContent, teamUser);
      }),
    );
  };

  /**
   * Saves the selected users and closes the modal. The behavior depends on the type.
   * @param {string} type - The type of users being saved, either 'client' or 'teams'.
   */
  async saveAndClose(type) {
    this.isSaving = true;
    const isClient = type === 'client';
    const prevIds = isClient ? this.step.client_users : this.step.teams_users;
    const updateFunction = isClient
      ? async (stepId, userIds) =>
          this.campaignsAPI.updateClientUsers(stepId, userIds)
      : async (stepId, userIds) =>
          this.campaignsAPI.updateTeamsUsers(stepId, userIds);

    const stepUUID = this.step.uuid_campaign_step;
    const newUsers = this.shadowRoot.querySelector('om2-two-pane-select').value;
    const ids = newUsers.map(user => user.PersonGUID);
    const filteredUsers = newUsers.filter(
      user => !prevIds?.includes(user.PersonGUID),
    );

    await updateFunction(stepUUID, ids);

    this.isSaving = false;

    this.dispatchNewEvent(
      isClient ? 'client-users-change' : 'teams-users-change',
      { detail: { ids } },
    );
    if (type !== 'client' && !!filteredUsers.length)
      this.#sendCard(filteredUsers);
    if (filteredUsers.length) {
      om2alert(
        isClient
          ? 'Your selected client user now has read and write access to this step.'
          : 'Your selected team members now have ownership this step.',
        {
          type: 'success',
          toast: true,
          closeTimeout: 3000,
        },
      );
    } else {
      om2alert(
        `You removed ${
          isClient ? 'client users' : 'team members'
        } from this step.`,
        {
          type: 'success',
          toast: true,
          closeTimeout: 3000,
        },
      );
    }
    this.#closeModal();
  }

  /**
   * Renders the button dropdown for user assignment, showing a counter for assigned users.
   * @return {html} The rendered html for the dropdown.
   */
  _renderBtnDropdown() {
    const teamsUsers = this.step?.teams_users?.length ?? 0;
    const clientUsers = this.step?.client_users?.length ?? 0;
    const totalUsers = teamsUsers + clientUsers;
    return html`
      <div
        class="${classMap({
          'is-active': this.dropdownIsActive,
          dropdown: true,
        })}"
        justify="end"
        ${ref(this.dropDownBtnRef)}
      >
        <div class="dropdown-trigger" slot="trigger">
          <omni-tooltip place="left">
            <div slot="invoker">
              <button
                ?disabled=${this.readonly}
                class="button is-text is-shadowless ${classMap({
                  'is-active': this.dropdownIsActive,
                })}"
                @click=${() => {
                  this.dropdownIsActive = !this.dropdownIsActive;
                }}
              >
                <omni-icon
                  class="is-size-1"
                  icon-id="omni:interactive:addUser"
                ></omni-icon>
              </button>
              ${totalUsers
                ? html`<span class="tiny-red-circle">${totalUsers}</span>`
                : nothing}
            </div>
            <div slot="content">Assign User</div>
          </omni-tooltip>
        </div>
        <div class="dropdown-menu" role="menu">
          <div class="dropdown-content">
            <button
              class="dropdown-item"
              @click="${() => {
                this.userTypeSelected = 'teams';
              }}"
            >
              Team member ${teamsUsers ? `(${teamsUsers} assigned)` : nothing}
            </button>
            <button
              class="dropdown-item "
              @click="${() => {
                this.userTypeSelected = 'client';
              }}"
            >
              Client user ${clientUsers ? `(${clientUsers} assigned)` : nothing}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Renders the modal for user assignment. The content depends on the type:
   * For 'client', it displays a list of client users.
   * For 'teams', it displays a list of teams users, fetched asynchronously.
   * @param {string} type - The type of users to assign, either 'client' or 'teams'.
   * @return {html} The rendered html for the modal.
   */
  _renderModal = type => {
    this.dropdownIsActive = false;

    const clientConfig = {
      selectedUsers: this.selectedClientUsers,
      stepUsers: this.step.client_users,
      users: this.clientUsers,
      title: 'Assign client user',
      bodySkeleton: 'skeleton',
      asyncSearch: null,
      leftPanePlaceholder: html`Search by client user's name to assign<br />them
        read and write access to this step`,
    };

    const teamsConfig = {
      selectedUsers: this.selectedTeamsUsers,
      stepUsers: this.step.teams_users,
      users: null,
      title: 'Assign team member',
      bodySkeleton: '',
      asyncSearch: search => this.#getTeamsMembers(search),
      leftPanePlaceholder: html`Search by team member's name to<br />assign them
        ownership to this step`,
    };

    const config = type === 'client' ? clientConfig : teamsConfig;

    const saveDisabled =
      this.isSaving ||
      !config.selectedUsers ||
      JSON.stringify(config.stepUsers) ===
        JSON.stringify(config.selectedUsers.map(u => u.PersonGUID));

    return html`
      <div class="modal is-active">
        <div
          aria-hidden="true"
          class="modal-background"
          @click="${() => this.#closeModal()}"
        ></div>
        <div class="modal-card" ${ref(this.modalRef)}>
          <header class="modal-card-head">
            <p class="modal-card-title">${config.title}</p>
          </header>
          <section
            class="modal-card-body ${config.users ? '' : config.bodySkeleton}"
          >
            ${config.users && config.users.users.length === 0
              ? html`<p class="has-text-danger has-text-centered mb-6">
                  No users exist for the current client
                </p>`
              : ''}
            <om2-two-pane-select
              hasObjectValues
              .options=${config.users?.users || []}
              .value=${config.selectedUsers}
              leftPaneLabel="Available users"
              rightPaneLabel="Selected users"
              .leftPanePlaceholder=${config.leftPanePlaceholder}
              rightPanePlaceholder="No user selected"
              searchPlaceholder="Search by name"
              idProperty="PersonGUID"
              nameProperty="Name"
              ?disabled=${config.users?.users.length === 0}
              @change=${e => {
                this.handleUserChange(e, type);
              }}
              .asyncSearch=${config.asyncSearch}
            >
            </om2-two-pane-select>
          </section>
          <footer class="modal-card-foot is-block">
            <div class="buttons is-right">
              <button
                @click="${() => this.#closeModal()}"
                class="button has-text-primary is-text"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="button is-primary"
                ?disabled=${saveDisabled}
                @click=${() => this.saveAndClose(type)}
              >
                Save
              </button>
            </div>
          </footer>
        </div>
      </div>
    `;
  };

  /**
   * @return {html} The rendered html for the entire component.
   */
  render() {
    return html`
      <omni-style>
        ${this._renderBtnDropdown()}
        ${this.userTypeSelected
          ? this._renderModal(this.userTypeSelected)
          : nothing}
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-assign-step-user', CampaignAssignStepUser);
